import React, { Component } from "react";
import {
  GET,
  GET_FILE,
  alert,
  saveAs,
  loading,
  NavbarCustomer,
  t,
  sortOptions,
  ActionIconButton,
  ACTION_ICONS,
} from "../../components/CustomComponent.js"; // ปรับ Path ให้ตรงกับโปรเจกต์ของคุณ
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler } from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";
import { getProfile } from "../../utils/localStorage";
import { isCustomerScopeReady } from "../../utils/customerScope";
import {
  REPORT_PERIODS,
  CHART_COLORS,
  formatDate,
  formatMoney,
  formatMonthLabel,
  formatPercent
} from "../../utils/customerReport";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler);

const LINE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true } },
};

const DONUT_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: { legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } } },
};

export default class CostReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // -----------------------------------
      // Filter States
      // -----------------------------------
      filter_period: "this_month",
      filter_category: "",
      filter_location: "",

      summary: {
        total_cost: 0,
        total_change_percent: null,
        labor_cost: 0,
        labor_rate: 0,
        parts_cost: 0,
        parts_rate: 0,
        service_fee: 0,
        service_fee_rate: 0,
      },

      trend: [],
      by_category: [],
      data: [],
      invoices: [],
      lookups: { categories: [], locations: [] },
      exporting: "",
      loaded: false,
    };
  }

  async componentDidMount() {
    if (!isCustomerScopeReady(getProfile())) {
      loading(false);
      return;
    }

    await Promise.all([this.GetLookups(), this.Get()]);
    loading(false);
  }

  GetLookups = async () => {
    const result = await GET("customer/reports/lookups");
    if (result?.status === true) this.setState({ lookups: result.data || { categories: [], locations: [] } });
  };

  filterParams = () => ({
    period: this.state.filter_period,
    category: this.state.filter_category || "",
    location: this.state.filter_location || "",
  });

  Get = async () => {
    loading(true);

    const result = await GET("customer/reports/cost-report", this.filterParams());

    if (result?.status === true) {
      const data = result.data || {};
      this.setState({
        summary: data.summary || this.state.summary,
        trend: Array.isArray(data.trend) ? data.trend : [],
        by_category: Array.isArray(data.by_category) ? data.by_category : [],
        data: Array.isArray(data.data) ? data.data : [],
        invoices: Array.isArray(data.invoices) ? data.invoices : [],
        loaded: true,
      });
    }

    loading(false);
  };

  exportFile = async (format) => {
    this.setState({ exporting: format });

    const result = await GET_FILE("customer/reports/cost-report/export", {
      ...this.filterParams(),
      format,
      lang: localStorage.getItem("lang") === "EN" ? "en" : "th",
    });

    this.setState({ exporting: "" });

    if (result?.status !== true) {
      alert("", "error", t("error"), result?.message || t("export_failed"));
      return;
    }

    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(result.blob, `cost-report_${stamp}.${format === "pdf" ? "pdf" : "xlsx"}`);
  };

  trendData = () => ({
    labels: this.state.trend.map((row) => formatMonthLabel(row.month)),
    datasets: [
      {
        label: t("total_cost"),
        data: this.state.trend.map((row) => row.total),
        borderColor: "#e67e22",
        backgroundColor: "rgba(230, 126, 34, 0.15)",
        fill: true,
        tension: 0.35,
      },
    ],
  });

  categoryData = () => ({
    labels: this.state.by_category.map((row) => row.key),
    datasets: [
      {
        data: this.state.by_category.map((row) => row.total),
        backgroundColor: this.state.by_category.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]),
        borderWidth: 0,
      },
    ],
  });

  changeText = () => {
    const value = this.state.summary.total_change_percent;
    if (value === null || value === undefined) return t("no_previous_period");
    return `${value > 0 ? "+" : ""}${value}% ${t("vs_previous")}`;
  };

  render() {
    const { summary, data, invoices, trend, by_category, lookups, filter_period, filter_category, filter_location, exporting, loaded } = this.state;

    return (
      <div
        className="w-100 min-vh-100 pb-5"
        style={{ backgroundColor: "#F4F7FE" }}
      >
        <NavbarCustomer />
        <div className="body px-4 mt-3">
          {/* Header Section */}
          <div className="mb-4">
            <h3 className="fw-bold mb-1 text-dark">
              {t("cost_report")}
            </h3>
            <div className="text-secondary text-14">
              {t("cost_report_desc")}
            </div>
          </div>

          {/* Filter Bar */}
          <div className="bg-white rounded-4 shadow-sm p-3 mb-4 d-flex flex-wrap align-items-end gap-3 border border-light">
            <div className="flex-grow-1" style={{ minWidth: "200px" }}>
              <label className="form-label text-secondary text-12 fw-bold mb-2">
                {t("period")}
              </label>
              <select
                className="form-select rounded-3 border-light bg-light text-dark fw-bold text-13 shadow-none"
                value={filter_period}
                onChange={(e) => this.setState({ filter_period: e.target.value })}
              >
                {REPORT_PERIODS.map((period) => (
                  <option key={period.value} value={period.value}>{t(period.label)}</option>
                ))}
              </select>
            </div>
            <div className="flex-grow-1" style={{ minWidth: "200px" }}>
              <label className="form-label text-secondary text-12 fw-bold mb-2">
                {t("category")}
              </label>
              <select
                className="form-select rounded-3 border-light bg-light text-dark fw-bold text-13 shadow-none"
                value={filter_category}
                onChange={(e) => this.setState({ filter_category: e.target.value })}
              >
                <option value="">{t("all_categories")}</option>
                {sortOptions(lookups.categories, { by: "name" }).map((category) => (
                  <option key={category.uid} value={category.name}>{category.name}</option>
                ))}
              </select>
            </div>
            <div className="flex-grow-1" style={{ minWidth: "200px" }}>
              <label className="form-label text-secondary text-12 fw-bold mb-2">
                {t("location")}
              </label>
              <select
                className="form-select rounded-3 border-light bg-light text-dark fw-bold text-13 shadow-none"
                value={filter_location}
                onChange={(e) => this.setState({ filter_location: e.target.value })}
              >
                <option value="">{t("all_locations")}</option>
                {sortOptions(lookups.locations).map((location) => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-primary px-4 fw-bold rounded-3 shadow-sm border-0 d-flex align-items-center"
              style={{ height: "42px", backgroundColor: "#e67e22" }}
              onClick={() => this.Get()}
            >
              <span className="icon me-2">{"\uf0b0"}</span> {t("apply_filters")}
            </button>
            <button
              className="btn btn-white border px-3 fw-bold rounded-3 d-flex align-items-center text-success shadow-none"
              style={{ height: "42px" }}
              disabled={!!exporting}
              onClick={() => this.exportFile("excel")}
            >
              <span className="icon me-2">{"\uf1c3"}</span> {exporting === "excel" ? t("loading") : t("export_excel")}
            </button>
          </div>

          {/* KPI Summary Cards */}
          <div className="row g-3 mb-4">
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 border-start border-5 border-primary rounded-4 shadow-sm p-3 bg-white h-100" style={{ borderLeftColor: "#e67e22 !important" }}>
                <div className="text-primary text-12 fw-bold mb-1" style={{ color: "#e67e22" }}>
                  {t("total_cost")}
                </div>
                <h2 className="fw-bold text-dark mb-1">{formatMoney(summary.total_cost)}</h2>
                <div className="text-primary text-10 fw-bold" style={{ color: "#e67e22" }}>{this.changeText()}</div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 border-start border-5 border-success rounded-4 shadow-sm p-3 bg-white h-100" style={{ borderLeftColor: "#3498db !important" }}>
                <div className="text-success text-12 fw-bold mb-1" style={{ color: "#3498db" }}>
                  {t("labor_cost")}
                </div>
                <h2 className="fw-bold text-dark mb-1">{formatMoney(summary.labor_cost)}</h2>
                <div className="text-success text-10 fw-bold" style={{ color: "#3498db" }}>
                  {formatPercent(summary.labor_rate)} {t("of_total")}
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 border-start border-5 border-warning rounded-4 shadow-sm p-3 bg-white h-100" style={{ borderLeftColor: "#2ecc71 !important" }}>
                <div className="text-warning text-12 fw-bold mb-1" style={{ color: "#2ecc71" }}>
                  {t("parts_cost")}
                </div>
                <h2 className="fw-bold text-dark mb-1">
                  {formatMoney(summary.parts_cost)}
                </h2>
                <div className="text-warning text-10 fw-bold" style={{ color: "#2ecc71" }}>
                  {formatPercent(summary.parts_rate)} {t("of_total")}
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 border-start border-5 border-danger rounded-4 shadow-sm p-3 bg-white h-100" style={{ borderLeftColor: "#9b59b6 !important" }}>
                <div className="text-danger text-12 fw-bold mb-1" style={{ color: "#9b59b6" }}>
                  {t("service_fee")}
                </div>
                <h2 className="fw-bold text-dark mb-1">{formatMoney(summary.service_fee)}</h2>
                <div className="text-danger text-10 fw-bold" style={{ color: "#9b59b6" }}>
                  {formatPercent(summary.service_fee_rate)} {t("of_total")}
                </div>
              </div>
            </div>
          </div>

          {/* Graphs Area */}
          <div className="row g-4 mb-4">
            <div className="col-lg-8">
              <div className="card border-0 rounded-4 shadow-sm p-4 bg-white h-100">
                <h6 className="fw-bold text-dark mb-4">{t("monthly_cost_trend")}</h6>
                <div style={{ height: "250px" }}>
                  {trend.length === 0 ? (
                    <div className="d-flex align-items-center justify-content-center bg-light rounded-3 h-100 text-secondary">
                      {loaded ? t("no_data") : t("loading")}
                    </div>
                  ) : (
                    <Line data={this.trendData()} options={LINE_OPTIONS} />
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card border-0 rounded-4 shadow-sm p-4 bg-white h-100">
                <h6 className="fw-bold text-dark mb-4">
                  {t("cost_by_category")}
                </h6>
                <div style={{ height: "250px" }}>
                  {by_category.length === 0 ? (
                    <div className="d-flex align-items-center justify-content-center h-100 text-secondary">
                      {loaded ? t("no_data") : t("loading")}
                    </div>
                  ) : (
                    <Doughnut data={this.categoryData()} options={DONUT_OPTIONS} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Details Table */}
          <div className="card border-0 rounded-4 shadow-sm pb-2 overflow-hidden bg-white mb-4">
            <div className="p-4 border-bottom border-light">
              <h6 className="fw-bold text-dark mb-0">
                {t("cost_by_asset")}
              </h6>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr className="text-secondary fw-bold text-12 border-bottom border-light bg-light bg-opacity-50">
                    <th className="py-3 px-4 border-0">{t("asset")}</th>
                    <th className="py-3 border-0">{t("category")}</th>
                    <th className="py-3 border-0">{t("labor")}</th>
                    <th className="py-3 border-0">{t("parts")}</th>
                    <th className="py-3 border-0">{t("service_fee")}</th>
                    <th className="py-3 border-0">{t("total")}</th>
                    <th className="py-3 border-0 text-center">% {t("of_total")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-secondary py-4">{loaded ? t("no_data") : t("loading")}</td>
                    </tr>
                  ) : data.map((item, idx) => (
                    <tr key={item.asset_uid || idx} className="border-bottom border-light">
                      <td className="py-3 px-4 fw-bold text-dark text-13">
                        {item.asset_name || "-"}
                      </td>
                      <td className="text-secondary text-13">{item.category || "-"}</td>
                      <td className="text-secondary text-13">
                        {formatMoney(item.labor_cost)}
                      </td>
                      <td className="text-secondary text-13">
                        {formatMoney(item.parts_cost)}
                      </td>
                      <td className="text-secondary text-13">
                        {formatMoney(item.service_fee)}
                      </td>
                      <td className="fw-bold text-13">
                        {formatMoney(item.total_cost)}
                      </td>
                      <td className="text-center text-secondary text-13">
                        {formatPercent(item.percent_of_total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Invoices Table */}
          <div className="border border-light rounded-4 overflow-hidden">
            <div className="p-3" style={{ backgroundColor: "#f8f9fa" }}>
              <h6 className="fw-bold text-dark mb-0 text-13">
                <span className="icon me-2">{"\uf51e"}</span>{" "}
                {t("recent_invoices")}
              </h6>
            </div>
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr className="text-secondary text-11 fw-bold bg-light bg-opacity-25">
                    <th className="py-3 px-4 border-0">{t("invoice_no")}</th>
                    <th className="py-3 border-0">{t("date")}</th>
                    <th className="py-3 border-0">{t("description")}</th>
                    <th className="py-3 border-0">{t("status")}</th>
                    <th className="py-3 border-0">{t("amount")}</th>
                    <th className="py-3 border-0">{t("action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-secondary py-4">{t("no_invoice_data")}</td>
                    </tr>
                  ) : invoices.map((item, idx) => (
                    <tr key={item.id || idx} className="border-bottom border-light">
                      <td className="py-3 px-4 fw-bold text-dark text-13" style={{ color: "#e67e22" }}>
                        {item.invoice_number}
                      </td>
                      <td className="text-secondary text-13">{formatDate(item.date)}</td>
                      <td className="text-secondary text-13">
                        {item.description}
                      </td>
                      <td>
                        <span className={`badge bg-${item.status === 'paid' ? 'success' : 'warning'}-light text-${item.status === 'paid' ? 'success' : 'warning'} rounded-pill px-3 py-1 fw-bold text-10 border`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="fw-bold text-13">{formatMoney(item.amount)}</td>
                      <td className="text-center">
                        <div className="d-flex align-items-center gap-2 justify-content-start">
                          <ActionIconButton label={t("download")} icon={ACTION_ICONS.download} variant="primary" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
